import React, { useCallback, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTrackEvent } from '@jotpsych/statsig';

import { usePayer,usePayerSearch } from './store/hooks';
import type { Payer } from './types';

/**
 * Payer Search Page
 * Allows users to search and view payer information from Stedi
 */
export const PayerSearchPage: React.FC = () => {
  const { trackEvent, trackError } = useTrackEvent();
  const [selectedPayerId, setSelectedPayerId] = useState<string | null>(null);
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    payers,
    isLoading,
    isFetching,
    isError,
    error,
    total,
    hasNextPage,
    loadNextPage,
    isSearching,
  } = usePayerSearch(300);
  const { payer: selectedPayer, isLoading: isLoadingPayer } = usePayer(selectedPayerId);

  // Track page view
  useEffect(() => {
    trackEvent('payer_search_page_viewed', {
      metadata: {},
    });
  }, [trackEvent]);

  // Track search (only when debounced search term changes and we have results)
  const prevDebouncedSearchRef = React.useRef<string>('');
  useEffect(() => {
    if (debouncedSearchTerm.length >= 2 && debouncedSearchTerm !== prevDebouncedSearchRef.current) {
      prevDebouncedSearchRef.current = debouncedSearchTerm;
      trackEvent('payer_search_performed', {
        metadata: {
          search_term_length: String(debouncedSearchTerm.length),
          results_count: String(payers.length),
        },
      });
    }
  }, [debouncedSearchTerm, payers.length, trackEvent]);

  // Track payer selection
  const handlePayerClick = useCallback(
    (payer: Payer) => {
      setSelectedPayerId(payer.id || null);
      trackEvent('payer_selected', {
        metadata: {
          payer_id: payer.id || 'unknown',
          has_avatar: String(!!payer.avatar_url),
          has_website: String(!!payer.website_url),
        },
      });
    },
    [trackEvent],
  );

  // Track errors
  useEffect(() => {
    if (isError && error) {
      trackError(error, 'payer_search');
    }
  }, [isError, error, trackError]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Payer Search
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search for insurance payers using Stedi's payer database
        </Typography>
      </Box>

      {/* Search Input */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search payers by name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        {searchTerm.length > 0 && searchTerm.length < 2 && (
          <Typography variant="caption" color="text.secondary">
            Enter at least 2 characters to search
          </Typography>
        )}
      </Paper>

      {/* Error State */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load payers. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!isLoading && !isError && (
        <>
          {isSearching && payers.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No payers found matching &quot;{debouncedSearchTerm}&quot;
            </Alert>
          )}

          {payers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {isSearching ? (
                  <>
                    Found {total} payer{total !== 1 ? 's' : ''} matching &quot;{debouncedSearchTerm}
                    &quot;
                  </>
                ) : (
                  <>
                    Showing {payers.length} of {total} payer{total !== 1 ? 's' : ''}
                  </>
                )}
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Payer List */}
            <Grid item xs={12} md={selectedPayerId ? 6 : 12}>
              {payers.length > 0 ? (
                <Box>
                  {payers.map(payer => (
                    <Card
                      key={payer.id || payer.stedi_id}
                      sx={{
                        mb: 2,
                        cursor: 'pointer',
                        border: selectedPayerId === payer.id ? 2 : 1,
                        borderColor: selectedPayerId === payer.id ? 'primary.main' : 'divider',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => handlePayerClick(payer)}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2}>
                          {payer.avatar_url && (
                            <Box
                              component="img"
                              src={payer.avatar_url}
                              alt={payer.display_name}
                              sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 1,
                                objectFit: 'cover',
                              }}
                            />
                          )}
                          <Box flex={1}>
                            <Typography variant="h6" component="h3">
                              {payer.display_name}
                            </Typography>
                            {payer.primary_payer_id && (
                              <Typography variant="caption" color="text.secondary">
                                ID: {payer.primary_payer_id}
                              </Typography>
                            )}
                            {payer.aliases && payer.aliases.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {payer.aliases.slice(0, 3).map((alias, idx) => (
                                  <Chip
                                    key={idx}
                                    label={alias}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Load More - only show for list view (not search) when more results available */}
                  {!isSearching && hasNextPage && (
                    <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
                      <Button
                        variant="outlined"
                        onClick={loadNextPage}
                        disabled={isFetching}
                        startIcon={isFetching ? <CircularProgress size={16} /> : undefined}
                      >
                        {isFetching ? 'Loading...' : 'Load More'}
                      </Button>
                    </Stack>
                  )}
                </Box>
              ) : !isSearching ? (
                <Alert severity="info">
                  No payers available. Try searching for a specific payer.
                </Alert>
              ) : null}
            </Grid>

            {/* Payer Details */}
            {selectedPayerId && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
                  {isLoadingPayer ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : selectedPayer ? (
                    <Box>
                      <Box display="flex" alignItems="center" gap={2} mb={2}>
                        {selectedPayer.avatar_url && (
                          <Box
                            component="img"
                            src={selectedPayer.avatar_url}
                            alt={selectedPayer.display_name}
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: 1,
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        <Box>
                          <Typography variant="h5" component="h2">
                            {selectedPayer.display_name}
                          </Typography>
                          {selectedPayer.primary_payer_id && (
                            <Typography variant="body2" color="text.secondary">
                              Primary ID: {selectedPayer.primary_payer_id}
                            </Typography>
                          )}
                          {selectedPayer.stedi_id && (
                            <Typography variant="body2" color="text.secondary">
                              Stedi ID: {selectedPayer.stedi_id}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {selectedPayer.website_url && (
                        <Box mb={2}>
                          <Link
                            href={selectedPayer.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit Website
                          </Link>
                        </Box>
                      )}

                      {selectedPayer.coverage_types && selectedPayer.coverage_types.length > 0 && (
                        <Box mb={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Coverage Types
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {selectedPayer.coverage_types.map((type, idx) => (
                              <Chip key={idx} label={type} size="small" />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {selectedPayer.operating_states &&
                        selectedPayer.operating_states.length > 0 && (
                          <Box mb={2}>
                            <Typography variant="subtitle2" gutterBottom>
                              Operating States
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1}>
                              {selectedPayer.operating_states.map((state, idx) => (
                                <Chip key={idx} label={state} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </Box>
                        )}

                      {selectedPayer.aliases && selectedPayer.aliases.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Aliases
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {selectedPayer.aliases.map((alias, idx) => (
                              <Chip key={idx} label={alias} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Alert severity="warning">Payer details not found</Alert>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Container>
  );
};

